import { CtaState } from "~/components/denomination_grid/denominations_grid";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { HubbleEvent } from "~/data/events";
import { clientRepo } from "~/server/apis/client_repo";
import { VoucherProduct } from "~/server/types/brand";
import {
  openRazorpayCheckout,
  RazorpayCheckoutProps,
  RazorpayMode,
} from "~/utils/third_party/razorpay";
import { openPayUCheckout } from "~/utils/third_party/payu";
import { showSnackBar } from "~/shared_states/snackbar";
import { useWebStoreContext } from "~/context/webstore_context";
import { config } from "~/data/config";
import { goBack, openModalV2 } from "~/shared_states/modal";
import { createMemo, createSignal } from "solid-js";
import { Header } from "~/types";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { ErrorMessage } from "~/data/consts";
import { generateDeviceVerificationToken } from "~/utils/common";
import { openPaymentLoaderTelescope } from "~/components/webstore_telescope/payment_loader_telescope";
import { PaymentLoader } from "~/screens/payment_options/payment_loader";
import { useNavigate } from "@solidjs/router";
import { openOrderFailedModal } from "~/components/webstore_telescope/order_failed_modal";
import useUserInfoModal from "./useUserInfoModal";

// these are incorrect imports, need to fix them

export interface OpenPaymentLoaderProps {
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
  orderId: string;
  reInit: () => void;
  onClose: () => void;
  brandImageUrl: string;
}

// this hook uses webstore context, should not be used outside of webstore
// this hook is used for only rnr payment, not for webstore payment
// so the user is always logged in
export function useRnrPayment(props: {
  brand: () => VoucherProduct;
  brandKey: string;
}) {
  const brandKey = props.brandKey;
  const [ctaState, setCtaState] = createSignal<CtaState>("DEFAULT");
  const [showFullPageLoadingOverlay, setShowFullPageLoadingOverlay] =
    createSignal(false);
  const navigate = useNavigate();

  const brand = props.brand;

  const analyticsService = useAnalyticsService();
  const userInfoModal = useUserInfoModal();

  const {
    storeState,
    refreshV4HomePage,
    getClientId,
    getPartnerConfigV3,
  } = useWebStoreContext();
  const clientId = getClientId;

  const shouldCollectUserData = () => {
    if (
      storeState.v4HomePage?.user &&
      storeState.v4HomePage?.user?.phoneNumber == null &&
      (getPartnerConfigV3().showUserDataCollectionModal || storeState.routeData?.clientConfig?.businessModel === "B2C")
    ) {
      return true;
    }
  };

  type OnClickGetThisCardProps = {
    amount: number;
    variantId: string | null;
    consumeCoins: boolean;
    denominationDetails?: { denomination: number; quantity: number }[];
  };

  async function onClickGetThisCard(props: OnClickGetThisCardProps) {
    if (shouldCollectUserData()) {
      return triggerUserDataCollectionFlow({
        onComplete: async () => {
          return onClickGetThisCard(props);
        },
      });
    }

    await initiateOrder(props);
  }

  function fullUserName() {
    let user = storeState.v4HomePage?.user;
    return `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  }

  const isClientRaisoni = createMemo(() => {
    return clientId()?.includes(config.clientIdPrefix?.raisoni);
  });

  function getUserDataCollectionModalSubtitle() {
    if (isClientRaisoni()) {
      return "Share your details to place an order";
    }
    return "Card details will be sent on your whatsapp & email";
  }

  const initiateOrder = async (props: OnClickGetThisCardProps) => {
    const { amount, variantId, consumeCoins, denominationDetails } = props;
    try {
      setCtaState("LOADING");

      let headers: Record<string, any> = {};
      if (storeState.v4HomePage?.user.fraudChecksEnabled) {
        const token = await generateDeviceVerificationToken();
        if (!token) {
          const err = new Error("FPJS token is undefined");
          captureErrorInSentry(err);
          showSnackBar({
            message: ErrorMessage.SECURITY_CHECK_FAILED,
            level: "error",
          });
          throw err;
        }
        headers[Header.FpDeviceToken] = token;
      }

      const initiateRedemptionResponse = await clientRepo.initiateRedemption(
        {
          amount: amount,
          paymentMode: "UPI",
          productId: brand()?.externalId!,
          ...(denominationDetails != null ? { denominationDetails } : {}),
          variantId: variantId ?? undefined,
          consumeCoins: consumeCoins,
        },
        headers
      );

      analyticsService.logEvent(HubbleEvent.PAYMENT_INITIATED, {
        brandName: brand()!.name.toLowerCase(),
        amount: amount,
        consumeCoins: consumeCoins,
        productId: brand()!.externalId,
      });

      function onPaymentFailed(failureReason: string) {
        analyticsService.logEvent(HubbleEvent.PAYMENT_FAIL, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
          reason: failureReason,
        });

        openOrderFailedModal({
          reason: failureReason,
        });
      }

      function onPaymentComplete() {
        analyticsService.logEvent(HubbleEvent.PAYMENT_SUCCESS, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
        });

        const orderId = encodeURIComponent(
          initiateRedemptionResponse.storeOrderDetails.id
        );
        const brandKey = encodeURIComponent(brand()!.brandKey);

        navigate(`/buy-gift-card/${brandKey}/order?orderId=${orderId}`, {
          resolve: true,
        });
      }

      function collectPaymentViaCustomCheckout() {
        openPaymentLoader({
          amount: initiateRedemptionResponse.depositPaymentDetails.amount,
          onPaymentComplete: async () => {
            goBack();
            await new Promise((resolve) => setTimeout(resolve, 300));
            onPaymentComplete();
          },
          onPaymentFailed: onPaymentFailed,
          orderId: initiateRedemptionResponse.depositPaymentDetails.id,
          reInit: () => initiateOrder(props),
          onClose: () => {
            goBack();
          },
          brandImageUrl: brand()!.voucherProductMetadata.logoUrl,
        });
      }

      async function collectPaymentViaRazorpay(_props: {
        depositId: string;
        amount: number;
      }) {
        let order;
        order = await clientRepo.payOrderStandardCheckout(_props.depositId);

        let razorpayCheckoutParams: RazorpayCheckoutProps = {
          amount: _props.amount,
          email: storeState.v4HomePage?.user?.email ?? "",
          orderId: order
            ? order.providerOrderId
            : (initiateRedemptionResponse?.depositPaymentDetails
              .providerMetadata?.providerOrderId ?? ""),
          phone: storeState.v4HomePage?.user?.phoneNumber ?? "",
          onSuccess: onPaymentComplete,
          onFailure: (response) => {
            onPaymentFailed(response.error.description);
          },
          mode: "UPI" as RazorpayMode,
        };

        openRazorpayCheckout(razorpayCheckoutParams);
      }

      async function collectPaymentViaPayu(props: { depositId: string }) {
        console.log(`collecting payment via Payu`);

        const order = await clientRepo.payOrderStandardCheckout(
          props.depositId
        );

        openPayUCheckout(order.providerCustomData.payU!, "UPI", {
          openedFrom: "webstore",
          orderId: initiateRedemptionResponse.storeOrderDetails.id,
          brandIdOrKey: brandKey,
        });
      }

      console.log(
        `init redemption response - provider is ${initiateRedemptionResponse.depositPaymentDetails.provider}`
      );

      if (
        initiateRedemptionResponse.depositPaymentDetails.status == "SUCCESS"
      ) {
        onPaymentComplete();
      } else {
        // This was done specifically for Raisoni portal where we didn't have Rzp whitelist
        // the portal url. Can be removed once Rzp is stable.
        if (
          initiateRedemptionResponse.depositPaymentDetails.provider == "PAYU"
        ) {
          collectPaymentViaPayu({
            depositId: initiateRedemptionResponse.depositPaymentDetails.id,
          });
        }
        // Always use moments for Rzp because collect in custom checkout is failing
        // TODO need to fix this
        else if (
          config.webstore.paymentGateway === "razorpay" ||
          config.env === "Prod"
        ) {
          await collectPaymentViaRazorpay({
            depositId: initiateRedemptionResponse.depositPaymentDetails.id,
            amount: initiateRedemptionResponse.depositPaymentDetails.amount,
          });
        } else {
          collectPaymentViaCustomCheckout();
        }
      }
      // State management moved to denomination grid
      setCtaState("DEFAULT");
    } catch (error: any) {
      showSnackBar({
        message: error.message,
        level: "error",
      });
      setCtaState("DEFAULT");

      // State management moved to denomination grid
    }
  };

  function triggerUserDataCollectionFlow(props: { onComplete: () => void }) {
    userInfoModal.openModal({
      clientLogoUrl: storeState.routeData?.clientConfig?.clientLogoUrl,
      // do not maintain dueAmount2 in internal state, tenur can change: do not depend on hardcoded value
      brandDetails: brand()?.voucherProductMetadata,
      email: storeState.v4HomePage?.user?.email ?? null,
      phone: storeState.v4HomePage?.user?.phoneNumber ?? null,
      name: fullUserName(),
      onClickSubmit: async (profileUpdateParams: any) => {
        analyticsService.logEvent("submit_user_details_in_brandl2", {
          brandName: brand()?.name.toLowerCase(),
          ...profileUpdateParams,
        });

        userInfoModal.setShowLoader(true);
        try {
          await clientRepo.updatePartnerUserProfile(profileUpdateParams);
          await refreshV4HomePage();
          await userInfoModal.closeModal();
          props.onComplete();
        } catch (e: any) {
          showSnackBar({
            level: "error",
            message: e.message ?? "Something went wrong",
          });
        } finally {
          userInfoModal.setShowLoader(false);
        }
      },
      subtitle: getUserDataCollectionModalSubtitle(),
    });
  }

  return {
    onClickGetThisCard,
    getDenominationList: () => undefined,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
  };
}

function openPaymentLoader(props: {
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
  orderId: string;
  reInit: () => void;
  onClose: () => void;
  brandImageUrl: string;
}) {
  // Use new telescope design system
  openPaymentLoaderTelescope({
    amount: props.amount,
    onPaymentComplete: props.onPaymentComplete,
    onPaymentFailed: props.onPaymentFailed,
    orderId: props.orderId,
    reInit: props.reInit,
    onClose: props.onClose,
    brandImageUrl: props.brandImageUrl,
  });

}
